import React, { useState, useEffect } from "react";
import "./AdmBookCategory.css";
import useBookCategories from "../../hooks/useBookCategories";
import { ApiUrl } from "../../../ApiUrl";
import Select from "react-select";
import ReactPaginate from "react-paginate";

const BookCategoryMaster = () => {
  const { categories, loading, error, fetchCategories } = useBookCategories();
  const [categoryName, setCategoryName] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [message, setMessage] = useState("");
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [editingSubCategoryId, setEditingSubCategoryId] = useState(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [bookCategories, setBookCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);
  const [currentPage, setCurrentPage] = React.useState(0); // ReactPaginate uses 0-based index
  const itemsPerPage = 10;

  // NEW: tracks whether the user has deliberately filtered the table via
  // the "Select Category" dropdown. This is separate from selectedCategoryId,
  // which is also used to populate the edit form, so we can't rely on
  // selectedCategoryId alone to decide what to refresh after a save.
  const [isFilterActive, setIsFilterActive] = useState(false);

  const pageCount = Math.ceil(filteredSubCategories.length / itemsPerPage);

  // Slice data for current page
  const paginatedSubCategories = filteredSubCategories.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage,
  );

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  useEffect(() => {
    fetchAllCategoriesWithSubCategories();
  }, []);

  const fetchAllCategoriesWithSubCategories = async () => {
    try {
      const response = await fetch(
        `${ApiUrl.apiurl}BookSubCategory/GetAllBookCategoryWithSubCategoryList/`,
      );
      const data = await response.json();
      if (data && data.message === "Success") {
        setBookCategories(data.data); // Set all categories and subcategories on page load
        setFilteredSubCategories(data.data); // Show all data initially
      } else {
        console.error("Failed to fetch categories:", data.message);
      }
    } catch (error) {
      console.error("An error occurred while fetching categories:", error);
    }
  };

  const isActiveValue = (value) => {
    if (typeof value === "string") {
      return value.trim().toUpperCase() === "Y";
    }
    return Boolean(value);
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategoryId(categoryId);
    const selectedCategory = categories.find(
      (cat) => cat.id === parseInt(categoryId),
    );
    setSelectedCategoryName(
      selectedCategory ? selectedCategory.CategoryName : "",
    );
    if (categoryId) {
      // User deliberately filtered the table to one category
      setIsFilterActive(true);
      fetchSubCategories(categoryId);
    } else {
      // "Select Categories" (empty) chosen -> show everything again
      setIsFilterActive(false);
      setFilteredSubCategories(bookCategories);
    }
    setCurrentPage(0); // ReactPaginate is 0-based
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      setLoadingSubCategories(true);
      const response = await fetch(
        `${ApiUrl.apiurl}BookSubCategory/GetBookSubCategoryBasedOnCategory/${categoryId}`,
      );
      const data = await response.json();
      if (data && data.message === "Success") {
        setFilteredSubCategories(data.data);
      } else {
        console.error("Failed to fetch subcategories.");
        setFilteredSubCategories([]);
      }
    } catch (error) {
      console.error("An error occurred while fetching subcategories:", error);
    } finally {
      setLoadingSubCategories(false);
    }
  };

  const handleCheckboxChange = (event) => {
    setIsEnabled(event.target.checked);
  };

  const handleSaveSubCategory = async () => {
    const orgId = localStorage.getItem("orgId");
    const branchId = localStorage.getItem("branchId");
    if (!subCategory) {
      setMessage("Error: Sub-category name cannot be empty.");
      return;
    }
    const data = {
      CategoryId: selectedCategoryId,
      SubCategoryName: subCategory,
      Subcategory_description: "cricket",
      org_id: orgId,
      branch_id: branchId,
      is_active: isEnabled ? "Y" : "N",
      created_by: 1,
    };
    try {
      let response;
      if (editingSubCategoryId) {
        // Update existing sub-category
        response = await fetch(
          `${ApiUrl.apiurl}BookSubCategory/BookSubCategoryupdate/${editingSubCategoryId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
          },
        );
        if (response.ok) {
          alert("Sub-category updated successfully!");
        }
      } else {
        // Create new sub-category
        response = await fetch(`${ApiUrl.apiurl}BookSubCategory/create/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        if (response.ok) {
          alert("Sub-category created successfully!");
        }
      }
      const result = await response.json();
      if (response.ok) {
        setMessage(result.message);
        setSubCategory("");
        setEditingSubCategoryId(null); // Clear edit mode after saving

        // Refresh the table based on whether the user actually applied a
        // category filter -- NOT based on whichever category the edited
        // row happened to belong to. This is the fix: previously this
        // always called fetchSubCategories(selectedCategoryId), which
        // silently narrowed the whole table down to just the edited
        // row's category after every save.
        if (isFilterActive) {
          await fetchSubCategories(selectedCategoryId);
        } else {
          await fetchAllCategoriesWithSubCategories();
        }
      } else {
        setMessage("Error: " + result.message);
      }
    } catch (error) {
      setMessage("Error: " + error.message);
    }
  };

  const handleEditClick = (
    subCategoryId,
    subCategoryName,
    isActive,
    categoryName,
  ) => {
    setEditingSubCategoryId(subCategoryId);
    setSubCategory(subCategoryName);
    setIsEnabled(isActiveValue(isActive));

    // Find categoryId from categoryName
    const matchedCategory = categories.find(
      (cat) => cat.CategoryName.toLowerCase() === categoryName.toLowerCase(),
    );

    if (matchedCategory) {
      // This only populates the form / dropdown display for editing.
      // It must NOT be treated as the user applying a table filter, so
      // we deliberately do not touch isFilterActive here.
      setSelectedCategoryId(String(matchedCategory.id));
      setSelectedCategoryName(matchedCategory.CategoryName);
    } else {
      console.warn("Category name not found in categories list");
      setSelectedCategoryId("");
    }
  };

  const handleSave = async () => {
    const orgId = localStorage.getItem("orgId");
    const branchId = localStorage.getItem("branchId");
    if (!categoryName) {
      setMessage("Error: Category name cannot be empty.");
      return;
    }
    if (
      categories.some(
        (category) =>
          category.CategoryName.toLowerCase() === categoryName.toLowerCase(),
      )
    ) {
      alert("Category already exists.");
      return;
    }
    const data = {
      CategoryName: categoryName,
      category_description: "",
      org_id: orgId,
      branch_id: branchId,
      is_active: "Y",
      created_by: 1,
    };
    try {
      const response = await fetch(`${ApiUrl.apiurl}BookCategory/create/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      const result = await response.json();
      if (response.ok) {
        alert("Category created successfully!");
        fetchCategories(); // Refresh the category list
        setCategoryName(""); // Clear input field
        setMessage("");
      } else {
        setMessage("Error: " + result.message);
      }
    } catch (error) {
      setMessage("Error: " + error.message);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="container-fluid mt-0">
      <div className="card p-4 mb-0">
        <h2 className="mb-1 text-center" style={{ fontSize: "x-large" }}>
          BOOK CATEGORY
        </h2>
        <h2 className="mb-1" style={{ fontSize: "x-large" }}>
          Add Book Category
        </h2>
        <div className="row align-items-center">
          <div className="col-md-3 mb-0">
            <label style={{ fontWeight: "bold" }}>Enter Category</label>
            <input
              type="text"
              className="form-control"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
            />
          </div>
          <div className="col-md-2 mb-0">
            <button className="btn btn-primary mt-3" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
        <div className="row">
          <h2 className="mb-1" style={{ fontSize: "x-large" }}>
            Add BookSub-Category
          </h2>
          <div className="col-md-3 mb-0">
            <label style={{ fontWeight: "bold" }}>Select Category</label>
            <Select
              options={[
                { value: "", label: "Select Categories" },
                ...categories.map((cat) => ({
                  value: cat.id,
                  label: cat.CategoryName,
                })),
              ]}
              onChange={(selectedOption) => {
                const event = {
                  target: { value: selectedOption?.value || "" },
                };
                handleCategoryChange(event);
              }}
              value={
                selectedCategoryId
                  ? {
                      value: selectedCategoryId,
                      label:
                        categories.find(
                          (cat) => cat.id === parseInt(selectedCategoryId),
                        )?.CategoryName || "",
                    }
                  : { value: "", label: "Select Categories" }
              }
            />
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-md-3 mb-0">
            <label style={{ fontWeight: "bold" }}>Sub-Category</label>
            <input
              type="text"
              className="form-control"
              value={subCategory}
              onChange={(e) => setSubCategory(e.target.value)}
            />
          </div>
          <div className="col-md-2 mb-0">
            <button
              className="btn btn-primary mt-3"
              onClick={handleSaveSubCategory}
            >
              Save
            </button>
          </div>
          <label>
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={handleCheckboxChange}
            />
            Enabled
          </label>
        </div>
        <h4>Book Category List</h4>
        <div className="table-responsive">
          <table className="table table-bordered ">
            <thead>
              <tr>
                <th>Sr.No</th>
                <th>Category</th>
                <th>Sub-Category</th>
                <th>Active</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSubCategories.length > 0 ? (
                paginatedSubCategories.map((subCategory, index) => (
                  <tr key={subCategory.id}>
                    <td>{index + 1 + currentPage * itemsPerPage}</td>
                    <td>{subCategory.categoryName}</td>
                    <td>{subCategory.SubCategoryName}</td>
                    <td>{isActiveValue(subCategory.is_active) ? "Y" : "N"}</td>
                    <td>
                      <button
                        className="btn btn-link"
                        onClick={() =>
                          handleEditClick(
                            subCategory.id,
                            subCategory.SubCategoryName,
                            subCategory.is_active,
                            subCategory.categoryName,
                          )
                        }
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No sub-categories available
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* ReactPaginate */}
          <ReactPaginate
            previousLabel={"Previous"}
            nextLabel={"Next"}
            breakLabel={"..."}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            onPageChange={handlePageClick}
            containerClassName={"pagination justify-content-center"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            activeClassName={"active"}
          />
        </div>
      </div>
    </div>
  );
};

const Pagination = ({
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };
  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };
  return (
    <div
      className="pagination-container"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <button
        onClick={handlePrevious}
        disabled={currentPage === 1}
        className="page-item"
      >
        Previous
      </button>
      {[...Array(totalPages)].map((_, index) => (
        <button
          key={index}
          className={`page-item ${index + 1 === currentPage ? "active" : ""}`}
          onClick={() => onPageChange(index + 1)}
        >
          {index + 1}
        </button>
      ))}
      <button
        onClick={handleNext}
        disabled={currentPage === totalPages}
        className="page-item"
      >
        Next
      </button>
    </div>
  );
};

export default BookCategoryMaster;
